import React, { useEffect, useRef } from 'react';
import { Card, Row, Col, Tooltip, Tag, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import * as echarts from 'echarts';

/**
 * 企业首页（数据看板）
 * 账务统计 + App运营 + 设备统计
 */

// ============ Mock 数据（TODO: 联调时替换为接口数据 GET /dashboard） ============
const MOCK_DASHBOARD = {
    accounting: {
        appRevenue: { cny: { current: 452800, yoyRate: 12.5 }, usd: { current: 64200, yoyRate: 8.3 } },
        profitIncome: { cny: { current: 158600, yoyRate: -3.2 }, usd: { current: 22400, yoyRate: -5.1 } },
        platformFee: { cny: { current: 89300, yoyRate: 15.8 }, usd: { current: 12600, yoyRate: 11.2 } },
        profitExpense: { cny: { current: 67400, yoyRate: 6.9 }, usd: { current: 9500, yoyRate: 4.7 } },
        platformExpense: { cny: { current: 121300, yoyRate: 9.4 } },
        trend7Days: [
            { date: '2026-08-06', incomeCny: 318500, incomeUsd: 45300, expenseCny: 142100, expenseUsd: 20100 },
            { date: '2026-08-07', incomeCny: 345200, incomeUsd: 48900, expenseCny: 156800, expenseUsd: 22300 },
            { date: '2026-08-08', incomeCny: 372800, incomeUsd: 53200, expenseCny: 168400, expenseUsd: 24100 },
            { date: '2026-08-09', incomeCny: 301400, incomeUsd: 42800, expenseCny: 135900, expenseUsd: 19200 },
            { date: '2026-08-10', incomeCny: 386900, incomeUsd: 55100, expenseCny: 174600, expenseUsd: 24900 },
            { date: '2026-08-11', incomeCny: 421300, incomeUsd: 59800, expenseCny: 182300, expenseUsd: 26100 },
            { date: '2026-08-12', incomeCny: 452800, incomeUsd: 64200, expenseCny: 188700, expenseUsd: 26800 },
        ],
    },
    appOperation: {
        totalUsers: 128460,
        yesterdayActive: 23410,
        yesterdayNew: 1890,
        orderCount: { today: { current: 1563, yoyRate: 18.6, compare: 1 } },
        orderAmount: {
            todayCny: { current: 893500, yoyRate: 12.4 },
            todayUsd: { current: 124300, yoyRate: 9.8 },
        },
        orderGiftCount: 87,
        funnel: {
            '本月': { visit: 12000, order: 3200, payment: 2100, subscribe: 860, orderRate: 26.7, paymentRate: 65.6, subRate: 41.0 },
            '上月': { visit: 10500, order: 2800, payment: 1750, subscribe: 700, orderRate: 26.7, paymentRate: 62.5, subRate: 40.0 },
        },
        activityTrend: [
            { date: '08-06', activeCount: 18650 },
            { date: '08-07', activeCount: 19210 },
            { date: '08-08', activeCount: 20180 },
            { date: '08-09', activeCount: 17820 },
            { date: '08-10', activeCount: 21450 },
            { date: '08-11', activeCount: 22580 },
            { date: '08-12', activeCount: 23410 },
        ],
        orderRevenueTrend: [
            { date: '2026-08-06', subscribeCny: 168200, subscribeUsd: 23900, nonSubscribeCny: 150300, nonSubscribeUsd: 21400 },
            { date: '2026-08-07', subscribeCny: 182400, subscribeUsd: 25900, nonSubscribeCny: 162800, nonSubscribeUsd: 23000 },
            { date: '2026-08-08', subscribeCny: 195600, subscribeUsd: 27800, nonSubscribeCny: 177200, nonSubscribeUsd: 25400 },
            { date: '2026-08-09', subscribeCny: 156800, subscribeUsd: 22300, nonSubscribeCny: 144600, nonSubscribeUsd: 20500 },
            { date: '2026-08-10', subscribeCny: 201500, subscribeUsd: 28600, nonSubscribeCny: 185400, nonSubscribeUsd: 26500 },
            { date: '2026-08-11', subscribeCny: 218700, subscribeUsd: 31000, nonSubscribeCny: 202600, nonSubscribeUsd: 28800 },
            { date: '2026-08-12', subscribeCny: 236900, subscribeUsd: 33600, nonSubscribeCny: 215100, nonSubscribeUsd: 30600 },
        ],
        userPayTrend: [
            { date: '2026-08-06', payingUsers: 3850, payRate: 20.6 },
            { date: '2026-08-07', payingUsers: 4120, payRate: 21.4 },
            { date: '2026-08-08', payingUsers: 4380, payRate: 21.7 },
            { date: '2026-08-09', payingUsers: 3640, payRate: 20.4 },
            { date: '2026-08-10', payingUsers: 4620, payRate: 21.6 },
            { date: '2026-08-11', payingUsers: 4890, payRate: 21.7 },
            { date: '2026-08-12', payingUsers: 5210, payRate: 22.3 },
        ],
    },
    device: {
        licenseTotal: 86500,
        producedCount: 74200,
        activeCount: 61800,
        bindCount: 53400,
        onlineCount: 42100,
        activationRate: 83.3,
        bindRate: 72.0,
        retentionRate: 56.8,
        onlineTrend7Days: [
            { date: '2026-08-06', onlineCount: 38600 },
            { date: '2026-08-07', onlineCount: 39400 },
            { date: '2026-08-08', onlineCount: 40500 },
            { date: '2026-08-09', onlineCount: 38200 },
            { date: '2026-08-10', onlineCount: 41100 },
            { date: '2026-08-11', onlineCount: 41700 },
            { date: '2026-08-12', onlineCount: 42100 },
        ],
    },
};

// ============ 工具函数 ============
const formatAmount = (val) => {
    if (val == null || val === 0) return '0.00';
    return (val / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatInt = (val) => {
    if (val == null) return '0';
    return Number(val).toLocaleString('zh-CN');
};

const yoyText = (vo) => {
    if (!vo || vo.yoyRate == null || vo.compare === 0) return '--';
    const rate = vo.yoyRate;
    return `${rate >= 0 ? '+' : ''}${rate.toFixed(1)}%`;
};

const yoyColor = (vo) => {
    if (!vo || vo.yoyRate == null) return '#909399';
    return vo.yoyRate >= 0 ? '#F56C6C' : '#67C23A';
};

// ============ 通用图表组件（echarts） ============
const EChart = ({ option, height = 300 }) => {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!chartRef.current) return undefined;
        const chart = echarts.init(chartRef.current);
        chart.setOption(option);
        const onResize = () => chart.resize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            chart.dispose();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <div ref={chartRef} style={{ width: '100%', height }} />;
};

const SectionTitle = ({ text, color = '#409EFF' }) => (
    <h2 style={{
        fontSize: 18, fontWeight: 600, color: '#303133', margin: '0 0 16px 0',
        paddingLeft: 12, borderLeft: `4px solid ${color}`,
    }}>
        {text}
    </h2>
);

// 账务统计：双币种指标卡（cnyOnly = 仅展示 CNY）
const CurrencyMetricCard = ({ label, data, cnyOnly }) => (
    <Card hoverable style={{ marginBottom: 12 }}>
        <p style={{ fontSize: 13, color: '#909399', margin: '0 0 8px 0' }}>{label}</p>
        {cnyOnly ? (
            <div style={{ fontSize: 22, fontWeight: 700, color: '#303133' }}>
                {formatAmount(data?.cny?.current)}
                <span style={{ fontSize: 12, marginLeft: 6, color: yoyColor(data?.cny) }}>{yoyText(data?.cny)}</span>
            </div>
        ) : (
            <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, color: '#909399', width: 12 }}>¥</span>
                    <span style={{ fontSize: 22, fontWeight: 700, color: '#303133' }}>{formatAmount(data?.cny?.current)}</span>
                    <span style={{ fontSize: 12, marginLeft: 6, color: yoyColor(data?.cny) }}>{yoyText(data?.cny)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 13, color: '#909399', width: 12 }}>$</span>
                    <span style={{ fontSize: 22, fontWeight: 700, color: '#303133' }}>{formatAmount(data?.usd?.current)}</span>
                    <span style={{ fontSize: 12, marginLeft: 6, color: yoyColor(data?.usd) }}>{yoyText(data?.usd)}</span>
                </div>
            </>
        )}
    </Card>
);

const DeviceTip = ({ text }) => (
    <Tooltip title={text}>
        <InfoCircleOutlined style={{ fontSize: 14, color: '#E6A23C', cursor: 'pointer' }} />
    </Tooltip>
);

// ============ 主页面 ============
const EnterpriseDashboard = () => {
    const data = MOCK_DASHBOARD;
    const accounting = data.accounting;
    const appOperation = data.appOperation;
    const device = data.device;

    const accountingMetrics = [
        { label: 'App云存收益', key: 'appRevenue' },
        { label: '分润收益(预估)', key: 'profitIncome' },
        { label: '平台抽佣(预估)', key: 'platformFee' },
        { label: '分润支出(预估)', key: 'profitExpense' },
        { label: '平台消费', key: 'platformExpense', cnyOnly: true },
    ];

    // 收支近7天趋势
    const trend7Option = {
        tooltip: { trigger: 'axis' },
        legend: { data: ['收入-CNY', '收入-USD', '支出-CNY', '支出-USD'], bottom: 0 },
        grid: { left: 55, right: 20, top: 20, bottom: 40 },
        xAxis: { type: 'category', data: accounting.trend7Days.map((d) => d.date.slice(5)), axisLabel: { fontSize: 11 } },
        yAxis: { type: 'value', axisLabel: { fontSize: 11 }, splitLine: { lineStyle: { type: 'dashed' } } },
        series: [
            { name: '收入-CNY', type: 'line', smooth: true, data: accounting.trend7Days.map((d) => (d.incomeCny / 100).toFixed(2)), lineStyle: { color: '#67C23A' }, itemStyle: { color: '#67C23A' }, areaStyle: { color: 'rgba(103,194,58,0.1)' } },
            { name: '收入-USD', type: 'line', smooth: true, data: accounting.trend7Days.map((d) => (d.incomeUsd / 100).toFixed(2)), lineStyle: { color: '#95D475' }, itemStyle: { color: '#95D475' }, areaStyle: { color: 'rgba(149,212,117,0.1)' } },
            { name: '支出-CNY', type: 'line', smooth: true, data: accounting.trend7Days.map((d) => (d.expenseCny / 100).toFixed(2)), lineStyle: { color: '#F56C6C' }, itemStyle: { color: '#F56C6C' }, areaStyle: { color: 'rgba(245,108,108,0.1)' } },
            { name: '支出-USD', type: 'line', smooth: true, data: accounting.trend7Days.map((d) => (d.expenseUsd / 100).toFixed(2)), lineStyle: { color: '#FAB6B6' }, itemStyle: { color: '#FAB6B6' }, areaStyle: { color: 'rgba(250,182,182,0.1)' } },
        ],
    };

    // 用户转化漏斗（本月 vs 上月）
    const funnelOption = (() => {
        const current = appOperation.funnel['本月'] || {};
        const compare = appOperation.funnel['上月'] || {};
        const maxVal = Math.max(current.visit || 0, compare.visit || 0) * 1.2;
        const funnelData = [
            { value: current.visit || 0, name: '访问', rate: current.orderRate || 0 },
            { value: current.order || 0, name: '下单', rate: current.paymentRate || 0 },
            { value: current.payment || 0, name: '付款', rate: current.subRate || 0 },
            { value: current.subscribe || 0, name: '订阅', rate: 0 },
        ];
        const compareData = [
            { value: compare.visit || 0, name: '访问' },
            { value: compare.order || 0, name: '下单' },
            { value: compare.payment || 0, name: '付款' },
            { value: compare.subscribe || 0, name: '订阅' },
        ];
        const makeSeries = (name, left, arr) => ({
            name, type: 'funnel', left, top: '10%', bottom: '15%', width: '40%', min: 0, max: maxVal,
            minSize: '0%', maxSize: '100%', sort: 'descending', gap: 2,
            label: { show: true, position: 'inside', formatter: (p) => `${p.name}\n${p.value}`, fontSize: 12 },
            itemStyle: { borderColor: '#fff', borderWidth: 1 },
            data: arr,
        });
        return {
            tooltip: {
                trigger: 'item',
                formatter: (params) => {
                    const d = params.data;
                    let result = `<div style="font-weight:bold;margin-bottom:4px;">${params.name}</div>`;
                    result += `<div>数量: <span style="font-weight:bold;">${d.value}</span></div>`;
                    if (d.rate > 0) result += `<div>转化率: <span style="font-weight:bold;color:#67C23A;">${d.rate.toFixed(1)}%</span></div>`;
                    return result;
                },
            },
            legend: { data: ['本月', '上月'], bottom: 0, textStyle: { fontSize: 11 } },
            series: [makeSeries('本月', '5%', funnelData), makeSeries('上月', '55%', compareData)],
        };
    })();

    // 用户活跃趋势（7天柱状图）
    const activityOption = {
        tooltip: { trigger: 'axis' },
        grid: { left: 45, right: 20, top: 20, bottom: 30 },
        xAxis: { type: 'category', data: appOperation.activityTrend.map((d) => d.date), axisLabel: { fontSize: 10 } },
        yAxis: { type: 'value' },
        series: [{ type: 'bar', data: appOperation.activityTrend.map((d) => d.activeCount), itemStyle: { color: '#409EFF', borderRadius: [4, 4, 0, 0] }, barMaxWidth: 30 }],
    };

    // 订单近7日收益（订阅/非订阅堆叠柱状图）
    const orderRevenueOption = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { data: ['订阅-CNY', '订阅-USD', '非订阅-CNY', '非订阅-USD'], bottom: 0, textStyle: { fontSize: 11 } },
        grid: { left: 55, right: 20, top: 20, bottom: 40 },
        xAxis: { type: 'category', data: appOperation.orderRevenueTrend.map((d) => d.date.slice(5)), axisLabel: { fontSize: 11 } },
        yAxis: { type: 'value', axisLabel: { fontSize: 11 }, splitLine: { lineStyle: { type: 'dashed' } } },
        series: [
            { name: '订阅-CNY', type: 'bar', stack: '订阅', data: appOperation.orderRevenueTrend.map((d) => (d.subscribeCny / 100).toFixed(2)), itemStyle: { color: '#409EFF' }, barMaxWidth: 36 },
            { name: '订阅-USD', type: 'bar', stack: '订阅', data: appOperation.orderRevenueTrend.map((d) => (d.subscribeUsd / 100).toFixed(2)), itemStyle: { color: '#79BBFF' }, barMaxWidth: 36 },
            { name: '非订阅-CNY', type: 'bar', stack: '非订阅', data: appOperation.orderRevenueTrend.map((d) => (d.nonSubscribeCny / 100).toFixed(2)), itemStyle: { color: '#67C23A' }, barMaxWidth: 36 },
            { name: '非订阅-USD', type: 'bar', stack: '非订阅', data: appOperation.orderRevenueTrend.map((d) => (d.nonSubscribeUsd / 100).toFixed(2)), itemStyle: { color: '#95D475' }, barMaxWidth: 36 },
        ],
    };

    // 用户近7天付费趋势（付费用户柱状 + 付费率折线 双轴）
    const userPayOption = {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' },
            formatter: (params) => {
                let result = `<div style="font-weight:bold;margin-bottom:8px;">${params[0].axisValue}</div>`;
                params.forEach((p) => {
                    const unit = p.seriesName === '付费率' ? '%' : '';
                    result += `<div style="display:flex;justify-content:space-between;gap:20px;"><span>${p.marker}${p.seriesName}</span><span style="font-weight:bold;">${p.value}${unit}</span></div>`;
                });
                return result;
            },
        },
        legend: { data: ['付费用户', '付费率'], bottom: 0, textStyle: { fontSize: 11 } },
        grid: { left: 50, right: 50, top: 20, bottom: 40 },
        xAxis: { type: 'category', data: appOperation.userPayTrend.map((d) => d.date.slice(5)), axisLabel: { fontSize: 11 } },
        yAxis: [
            { type: 'value', axisLabel: { fontSize: 11 }, splitLine: { lineStyle: { type: 'dashed' } } },
            { type: 'value', axisLabel: { fontSize: 11, formatter: '{value}%' }, splitLine: { show: false } },
        ],
        series: [
            { name: '付费用户', type: 'bar', data: appOperation.userPayTrend.map((d) => d.payingUsers), itemStyle: { color: '#409EFF' } },
            { name: '付费率', type: 'line', yAxisIndex: 1, smooth: true, data: appOperation.userPayTrend.map((d) => d.payRate.toFixed(1)), lineStyle: { color: '#F56C6C', width: 2 }, itemStyle: { color: '#F56C6C' }, areaStyle: { color: 'rgba(245,108,108,0.1)' } },
        ],
    };

    // 近7日在线设备趋势
    const deviceOnlineOption = {
        tooltip: { trigger: 'axis' },
        grid: { left: 50, right: 20, top: 20, bottom: 30 },
        xAxis: { type: 'category', data: device.onlineTrend7Days.map((d) => d.date.slice(5)), axisLabel: { fontSize: 11 } },
        yAxis: { type: 'value', axisLabel: { fontSize: 11 } },
        series: [{
            name: '在线设备', type: 'line', smooth: true, symbol: 'circle', symbolSize: 6,
            data: device.onlineTrend7Days.map((d) => d.onlineCount),
            lineStyle: { color: '#E6A23C', width: 2 }, itemStyle: { color: '#E6A23C' },
            areaStyle: { color: 'rgba(230,162,60,0.1)' },
        }],
    };

    return (
        <div style={{ padding: '16px 20px' }}>
            <div style={{ marginBottom: 20 }}>
                <h1 style={{ fontSize: 20, fontWeight: 600, color: '#303133', margin: 0 }}>昨日数据统计</h1>
            </div>

            {/* ============ 账务统计 ============ */}
            <div style={{ marginBottom: 24 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 4 }}>
                    <Col><SectionTitle text="账务统计" /></Col>
                    <Col><span style={{ fontSize: 16, fontWeight: 600, color: '#303133' }}>昨日(按UTC统计)数据</span></Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} lg={16}>
                        <Row gutter={16}>
                            {accountingMetrics.map((m) => (
                                <Col key={m.key} xs={24} sm={12} md={6}>
                                    <CurrencyMetricCard label={m.label} data={accounting[m.key]} cnyOnly={m.cnyOnly} />
                                </Col>
                            ))}
                            <Col xs={24} sm={12} md={6}>
                                <Card hoverable style={{ marginBottom: 12, borderLeft: '3px solid #E6A23C' }}>
                                    <p style={{ fontSize: 13, color: '#909399', margin: '0 0 8px 0' }}>赠送订单</p>
                                    <div style={{ fontSize: 22, fontWeight: 700, color: '#303133' }}>{formatInt(appOperation.orderGiftCount)}</div>
                                    <span style={{ fontSize: 12, color: '#909399' }}>当日赠送订单数</span>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Card>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#303133', marginBottom: 8 }}>收支近7天趋势</div>
                            <EChart option={trend7Option} />
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* ============ App运营 ============ */}
            <div style={{ marginBottom: 24 }}>
                <SectionTitle text="App运营" color="#67C23A" />
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col xs={24} md={12}>
                        <Card hoverable style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <span style={{ fontSize: 20, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: '#E8F4FD' }}>👥</span>
                                <span style={{ fontSize: 14, fontWeight: 600, color: '#303133' }}>用户指标</span>
                            </div>
                            <div style={{ paddingTop: 8, borderTop: '1px solid #EBEEF5' }}>
                                <Row>
                                    <Col span={8} style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 13, color: '#909399', marginBottom: 4 }}>总用户数</div>
                                        <div style={{ fontSize: 20, fontWeight: 700, color: '#303133' }}>{formatInt(appOperation.totalUsers)}</div>
                                    </Col>
                                    <Col span={8} style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 13, color: '#909399', marginBottom: 4 }}>昨日活跃</div>
                                        <div style={{ fontSize: 20, fontWeight: 700, color: '#303133' }}>{formatInt(appOperation.yesterdayActive)}</div>
                                    </Col>
                                    <Col span={8} style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 13, color: '#909399', marginBottom: 4 }}>昨日新增</div>
                                        <div style={{ fontSize: 20, fontWeight: 700, color: '#303133' }}>{formatInt(appOperation.yesterdayNew)}</div>
                                    </Col>
                                </Row>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} md={12}>
                        <Card hoverable style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <span style={{ fontSize: 20, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: '#F0F9EB' }}>📊</span>
                                <span style={{ fontSize: 14, fontWeight: 600, color: '#303133' }}>订单指标</span>
                            </div>
                            <div style={{ paddingTop: 8, borderTop: '1px solid #EBEEF5' }}>
                                {[
                                    { label: '订单总数', value: formatInt(appOperation.orderCount?.today?.current), vo: appOperation.orderCount?.today },
                                    { label: '订单总额(CNY)', value: `¥${formatAmount(appOperation.orderAmount?.todayCny?.current)}`, vo: appOperation.orderAmount?.todayCny },
                                    { label: '订单总额(USD)', value: `$${formatAmount(appOperation.orderAmount?.todayUsd?.current)}`, vo: appOperation.orderAmount?.todayUsd },
                                ].map((m) => (
                                    <div key={m.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                        <span style={{ fontSize: 13, color: '#909399' }}>{m.label}</span>
                                        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                            <span style={{ fontSize: 20, fontWeight: 700, color: '#303133' }}>{m.value}</span>
                                            <span style={{ fontSize: 12, color: yoyColor(m.vo) }}>日同比 {yoyText(m.vo)}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </Col>
                </Row>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col xs={24} md={12}>
                        <Card style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#303133', marginBottom: 8 }}>用户转化漏斗</div>
                            <EChart option={funnelOption} />
                        </Card>
                    </Col>
                    <Col xs={24} md={12}>
                        <Card style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#303133', marginBottom: 8 }}>用户活跃趋势（7天）</div>
                            <EChart option={activityOption} />
                        </Card>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Card style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#303133', marginBottom: 8 }}>订单近7日收益</div>
                            <EChart option={orderRevenueOption} />
                        </Card>
                    </Col>
                    <Col xs={24} md={12}>
                        <Card style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#303133', marginBottom: 8 }}>用户近7天付费趋势</div>
                            <EChart option={userPayOption} />
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* ============ 设备统计 ============ */}
            <div style={{ marginBottom: 24 }}>
                <SectionTitle text="设备统计" color="#E6A23C" />
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col xs={12} sm={8} md={4}>
                        <Card hoverable style={{ marginBottom: 12 }}>
                            <p style={{ fontSize: 13, color: '#909399', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                                License总数 <DeviceTip text="企业授权码的总数，即所有License汇总" />
                            </p>
                            <p style={{ fontSize: 22, fontWeight: 700, color: '#303133', margin: 0 }}>{formatInt(device.licenseTotal)}</p>
                        </Card>
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                        <Card hoverable style={{ marginBottom: 12 }}>
                            <p style={{ fontSize: 13, color: '#909399', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                                生产数量 <DeviceTip text="使用平台产测工具，License烧录到设备中 + 非平台产测的License（因为未上报烧录状态，所以工单按已生产统计）" />
                            </p>
                            <p style={{ fontSize: 22, fontWeight: 700, color: '#303133', margin: 0 }}>{formatInt(device.producedCount)}</p>
                        </Card>
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                        <Card hoverable style={{ marginBottom: 12 }}>
                            <p style={{ fontSize: 13, color: '#909399', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                                激活数量 <DeviceTip text="设备首次联网即视为激活，激活率=激活数量/生产数量×100%" />
                            </p>
                            <p style={{ fontSize: 22, fontWeight: 700, color: '#303133', margin: 0 }}>{formatInt(device.activeCount)}</p>
                            <p style={{ fontSize: 12, color: '#67C23A', marginTop: 4 }}>激活率 {device.activationRate?.toFixed(1)}%</p>
                        </Card>
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                        <Card hoverable style={{ marginBottom: 12 }}>
                            <p style={{ fontSize: 13, color: '#909399', margin: '0 0 6px 0' }}>绑定数量</p>
                            <p style={{ fontSize: 22, fontWeight: 700, color: '#303133', margin: 0 }}>{formatInt(device.bindCount)}</p>
                            <p style={{ fontSize: 12, color: '#67C23A', marginTop: 4 }}>绑定率 {device.bindRate?.toFixed(1)}%</p>
                        </Card>
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                        <Card hoverable style={{ marginBottom: 12 }}>
                            <p style={{ fontSize: 13, color: '#909399', margin: '0 0 6px 0' }}>在线数量</p>
                            <p style={{ fontSize: 22, fontWeight: 700, color: '#303133', margin: 0 }}>{formatInt(device.onlineCount)}</p>
                            <p style={{ fontSize: 12, color: '#67C23A', marginTop: 4 }}>留存率 {device.retentionRate?.toFixed(1)}%</p>
                        </Card>
                    </Col>
                </Row>
                <Card>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#303133', marginBottom: 8 }}>近7日在线设备趋势</div>
                    <EChart option={deviceOnlineOption} />
                </Card>
            </div>

            <Space style={{ marginBottom: 8 }}>
                <Tag color="orange">Mock 数据</Tag>
                <span style={{ fontSize: 12, color: '#999' }}>联调时替换为接口 GET /dashboard 数据</span>
            </Space>
        </div>
    );
};

export default EnterpriseDashboard;
